import { useEffect, useState } from "react";
import TourCard from "@/components/TourCard";
import { fetchTours } from "@/lib/api";
import { TOURS } from "@/constants/testIds";
import { GraduationCap, Globe2, Sparkles, Calendar } from "lucide-react";

const FILTERS = [
  { key: "all", label: "All Tours", icon: Sparkles, testId: TOURS.filterAll },
  { key: "HBCU Tour", label: "HBCU Tours", icon: GraduationCap, testId: "tours-filter-hbcu" },
  { key: "College Tour", label: "College Tours", icon: GraduationCap, testId: TOURS.filterCollege },
  { key: "Day Tour", label: "One-Day Tours", icon: Calendar, testId: "tours-filter-day" },
  { key: "Group Travel", label: "Group Travel", icon: Globe2, testId: TOURS.filterGroup },
];

const HEADERS = {
  all: { eyebrow: "The Catalog", title: <>Single-day to multi-day, around the <span className="hand-accent text-terracotta">country</span>.</>, sub: "Browse all of our starting-point trips — college tours from a single day to multi-day, group travel to dream destinations, and one-day campus visits. Every detail flexes around your group's dates, goals, and budget." },
  "College Tour": { eyebrow: "College Tours", title: <>HBCU · Big Ten · Florida — <span className="hand-accent text-terracotta">NYC &amp; D.C. our specialty</span>.</>, sub: "College tours across the country — historic HBCUs, Big Ten powerhouses, Florida schools, with New York City and Washington D.C. as our two specialty regions. We can take your group to schools anywhere." },
  "HBCU Tour": { eyebrow: "HBCU Tours", title: <>Historic HBCUs. <span className="hand-accent text-terracotta">All on the table.</span></>, sub: "Morehouse, Spelman, Clark Atlanta, Tuskegee, Howard, Norfolk State, Hampton — and any others your group wants on the route. We mix HBCUs with other schools too." },
  "Day Tour": { eyebrow: "One-Day Tours", title: <>Two colleges. <span className="hand-accent text-terracotta">One day.</span></>, sub: "Not ready for an overnight trip? Our one-day college tours feature two colleges in a single day — round-trip transport, lunch, and admissions sessions included." },
  "Group Travel": { eyebrow: "Group Travel", title: <>Dream destinations, <span className="hand-accent text-terracotta">handled</span>.</>, sub: "For groups of 25 or more. Thailand, Mexico, Africa, the Caribbean, New York, Washington D.C., Miami — wherever your group is dreaming of, we'll build the trip." },
};

export default function Tours({ initialFilter = "all" }) {
  const [tours, setTours] = useState([]);
  const [active, setActive] = useState(initialFilter);
  const [loading, setLoading] = useState(true);

  useEffect(() => setActive(initialFilter), [initialFilter]);

  useEffect(() => {
    setLoading(true);
    fetchTours(active)
      .then(setTours)
      .catch(() => setTours([]))
      .finally(() => setLoading(false));
  }, [active]);

  const header = HEADERS[active] || HEADERS.all;

  return (
    <>
      <section className="container-edge pt-20 md:pt-24 pb-6">
        <div className="eyebrow mb-4"><Sparkles size={14} className="fill-coral" /> {header.eyebrow}</div>
        <h1 className="display-serif text-5xl sm:text-6xl leading-[0.98] max-w-3xl">
          {header.title}
        </h1>
        <p className="mt-6 max-w-2xl text-muted2 text-base leading-relaxed">{header.sub}</p>
      </section>

      {/* Examples disclaimer banner */}
      <section className="container-edge pb-2">
        <div className="bg-gold/25 border-2 border-gold/50 rounded-3xl p-6 md:p-7 flex flex-col md:flex-row items-start md:items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-gold flex items-center justify-center shrink-0">
            <Sparkles size={22} className="text-ink" />
          </div>
          <div className="flex-1">
            <div className="font-display font-semibold text-lg md:text-xl text-ink leading-tight mb-1">
              These are example tours — all dates &amp; routes are subject to availability.
            </div>
            <p className="text-sm text-ink/75 leading-relaxed">
              The tours shown below are sample itineraries to give you ideas. We cover <strong>colleges and destinations of your choice upon request</strong> — built around your group's dates, budget, and goals.
            </p>
          </div>
        </div>
      </section>

      <section className="container-edge py-6">
        <div className="flex flex-wrap gap-2">
          {FILTERS.map(({ key, label, icon: Icon, testId }) => (
            <button
              key={key}
              data-testid={testId}
              onClick={() => setActive(key)}
              className={`inline-flex items-center gap-2 px-5 py-2.5 font-body text-sm font-medium rounded-full border-2 transition-all ${
                active === key
                  ? "bg-ink text-cream border-ink"
                  : "bg-cream text-ink/75 border-ink/15 hover:border-ink hover:text-ink"
              }`}
            >
              <Icon size={14} />
              {label}
            </button>
          ))}
        </div>
      </section>

      <section className="container-edge pb-24">
        <div data-testid={TOURS.list} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading
            ? Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-[440px] bg-white rounded-3xl ring-1 ring-ink/8 animate-pulse" />
              ))
            : tours.map((t, i) => (
                <div key={t.slug} data-testid={TOURS.card(t.slug)}>
                  <TourCard tour={t} index={i} />
                </div>
              ))}
        </div>
        {!loading && tours.length === 0 && (
          <div className="text-center py-20 text-ink/60 font-mono text-sm uppercase tracking-[0.2em]">
            No tours match this filter.
          </div>
        )}
      </section>
    </>
  );
}
